import _ from 'underscore';

import { ListResult } from '../../ts/fetchers/ListResult';
import { ISliceable } from '../../ts/models/ISliceable';

/*
 * SparseCachedList
 *
 * Caches (possibly sparse) blocks of data
 *
 */
export class SparseCachedList<T, TSliceableItem> {

  public static DefaultBlockSize = 1000;

  private cachedData: T[] = null;

  private fetching: boolean[] = [];

  constructor(private sliceable: ISliceable<TSliceableItem>,
              private transform: (item: TSliceableItem) => T,
              private blockSize: number = SparseCachedList.DefaultBlockSize) {}

  // Fetches blocks as necessary to cover the range from 'start' to 'end'.
  // Returns a promise which resolves with the entire cached data array.
  public getItems(start: number, end: number): Promise<T[]> {
    start = Math.max(start, 0);
    if (this.cachedData) {

      // Empty data-set
      if (this.cachedData.length === 0) {
        return Promise.resolve(this.cachedData);
      }

      end = Math.min(end, this.cachedData.length - 1);
    }

    const fetches: Promise<T[]>[] = [];
    const blockSize = this.blockSize;

    const firstBlockToFetch = Math.floor(start / blockSize);
    const lastBlockToFetch = Math.floor(end / blockSize);

    for(let blockIndex = firstBlockToFetch; blockIndex <= lastBlockToFetch; blockIndex++) {
      if (this.fetching[blockIndex] !== true) {
        this.fetching[blockIndex] = true;
        fetches.push(this.fetchBlock(blockIndex));
      }
    }

    return Promise.all(fetches).then(() => {
      return this.cachedData;
    });
  }

  // Fetches blocks as necessary to cover the range from 'start' to 'end'.
  // calls the rowHandler with a promise which resolves with the entire cached data array.
  public getItemsAsync(start: number, end: number, rowHandler: (rowsPromise: Promise<T[]>) => void): void {
    rowHandler(new Promise<T[]>((resolve, reject) => {
      resolve(this.getItems(start, end));
    }));
  }

  private fetchBlock(blockIndex: number): Promise<T[]> {
    return new Promise<T[]>((resolve, reject) => {
      this.sliceable.getSlice(blockIndex * this.blockSize, this.blockSize)
        .then((result: ListResult<TSliceableItem>) => {

            // Update fetched data

            // CONSIDER: use List from immutable.js to avoid expensive copying
            const newCachedData = _.clone(this.cachedData || []);

            newCachedData.length = result.total;
            for (let i = 0; i < result.items.length; i++) {
              newCachedData[result.index + i] = this.transform(result.items[i]);
            }
            this.cachedData = newCachedData;
            resolve(newCachedData);
          }
        )
        .catch(error => reject(error));
    });
  }

}

export function allItemsExistInRange<T>(items: ReadonlyArray<T>, startItemIndex: number, endItemIndex: number) {
  const maxIndex = Math.min(endItemIndex, items.length - 1);
  for (let i = startItemIndex; i <= maxIndex; i++) {
    if (items[i] == null || items[i] === undefined) {
      return false;
    }
  }
  return true;
}
